import type { Priority } from "../tasks/types.js";

/**
 * The two-way convention that lets a local TASK live as a Google Calendar EVENT
 * (so time / duration / recurrence stay lossless) while still carrying its
 * task-only semantics — type, completion, subtasks, priority, deadline.
 *
 * TWO STRICTLY SEPARATED CHANNELS (see the redesign plan):
 *  - MACHINE STATE (authoritative): `extendedProperties.private` carries
 *    `tcTask`/`tcVer`/`tcState`. Google stores ext-props verbatim, so this never
 *    suffers whitespace normalisation. Priority, deadline, and subtask ids/state
 *    are READ from here — never from the description text.
 *  - HUMAN PROJECTION: the description's managed block. We WRITE known subtasks as
 *    emoji (`☐`/`☑`) — a one-way echo that is IGNORED on parse. The ONLY signal we
 *    consume from the description is the explicit BRACKET DIALECT (`[ ]`/`[x]`/`[]`):
 *    a bracket line whose title matches a known subtask = a toggle; a new title =
 *    a create. Each user bracket is consumed once and re-canonicalised to emoji on
 *    the next push, which is what makes the system converge in ≤2 cycles.
 *
 * The title marker stays the parent's done channel (`⬜`/`✅`), tolerant of
 * user-typed `[ ]`/`[x]`/`Task:` which we normalise to the emoji on the next push.
 *
 * Pure (no IO).
 */

const OPEN_MARK = "⬜";
const DONE_MARK = "✅";
/** Subtask emoji (machine echo, never parsed back). */
const SUB_OPEN = "☐";
const SUB_DONE = "☑";

/** Strict, full-text delimiters for the managed block — Google won't strip them. */
const BLOCK_OPEN = "──────── YourCalendar · task details ────────";
const BLOCK_CLOSE = "──────── end · YourCalendar ────────";
/** Legacy sentinels recognised on parse so already-synced events migrate without
 *  a resync: the pre-rebrand "theCalendar" open/close pair, plus the older v2
 *  single header. Remove once no old events remain in the wild. */
const LEGACY_OPEN = "──────── theCalendar · task details ────────";
const LEGACY_CLOSE = "──────── end · theCalendar ────────";
const LEGACY_HEADER = "──── TheCalendar · task details (edit the boxes) ────";

export const TC_EXT_TASK = "tcTask";
export const TC_EXT_VER = "tcVer";
export const TC_EXT_STATE = "tcState";
/**
 * WRITE NONCE — a content hash of the exact event body we last wrote, stamped
 * into the body itself. It is the echo-suppression channel: on the next pull we
 * recompute the hash the local task WOULD produce, and if it matches the remote
 * event's `tcRev`, the remote is byte-identical to our own last write and there
 * is nothing to apply inbound, whatever the etag says.
 *
 * This matters because `etag` changes for reasons that are not user edits
 * (Google-side normalisation, a re-save from another client, our own PATCH whose
 * etag adoption raced something else). Treating every etag change as "the user
 * edited this in Google" meant a full inbound overwrite of the local row, which
 * is how an unpushed local time change silently reverted.
 */
export const TC_EXT_REV = "tcRev";
/** Legacy v2 key — read-only fallback so old events upgrade lazily. */
const TC_EXT_SUBTASKS = "tcSubtasks";

/** Current machine-state format version. Bumped only when the `tcState` shape or
 *  the stamped-property set changes; a stale `ver` lazily triggers a per-event
 *  reformat via the write-back (never a sync-token clear / full resync).
 *  v4 = `tcRev` write nonce added (also stamped on plain events). */
export const CURRENT_VER = "4";

export interface ParsedTitle {
  /** True when the title carried a task marker (⬜/✅/[ ]/[x]/Task:). */
  isTask: boolean;
  done: boolean;
  /** The title with any marker stripped. */
  title: string;
}

/** Subtask identity + state, the unit of machine truth in `tcState`. */
export interface SubtaskRef {
  id: string;
  title: string;
  done: boolean;
}

/** The machine state we encode into `extendedProperties.private`. The parent's
 *  done-state + title live in the event title, not here. */
export interface TaskState {
  priority: Priority;
  /** YYYY-MM-DD or null. */
  deadline: string | null;
  /** All undated descendants, flat (DFS order), no depth. */
  subtasks: SubtaskRef[];
  /** Human-readable parent reference for dated subtasks ("Buy groceries · Mon …"). */
  parentInfo?: string;
}

/** A user-typed bracket line decoded from a task event's description. */
export interface BracketEdit {
  title: string;
  done: boolean;
}

// ── Title ───────────────────────────────────────────────────────────────────

export function formatTaskTitle(task: { title: string; is_event: boolean; status: string }): string {
  const clean = (task.title ?? "").trim();
  if (task.is_event) return clean;
  const mark = task.status === "done" ? DONE_MARK : OPEN_MARK;
  return clean ? `${mark} ${clean}` : mark;
}

export function parseTaskTitle(raw: string): ParsedTitle {
  const s = (raw ?? "").trimStart();
  for (const m of [DONE_MARK, "[x]", "[X]"]) {
    if (s.startsWith(m)) return { isTask: true, done: true, title: s.slice(m.length).trim() };
  }
  for (const m of [OPEN_MARK, "[ ]", "[]"]) {
    if (s.startsWith(m)) return { isTask: true, done: false, title: s.slice(m.length).trim() };
  }
  const t = /^task:\s*/i.exec(s);
  if (t) return { isTask: true, done: false, title: s.slice(t[0].length).trim() };
  return { isTask: false, done: false, title: raw ?? "" };
}

// ── Managed block (human projection) ─────────────────────────────────────────

export function emptyState(): TaskState {
  return { priority: "none", deadline: null, subtasks: [] };
}

function hasBlockContent(s: TaskState): boolean {
  return s.subtasks.length > 0 || s.priority !== "none" || !!s.deadline || !!s.parentInfo;
}

/** User notes + a strictly-delimited machine block. Subtasks render as emoji
 *  (echo); priority/deadline/"Part of:" are read-only display lines. Returns just
 *  the trimmed notes when there's nothing to project. */
export function buildManagedBlock(userNotes: string, state: TaskState): string {
  const head = (userNotes ?? "").trim();
  if (!hasBlockContent(state)) return head;
  const lines: string[] = [BLOCK_OPEN];
  if (state.parentInfo) lines.push(`Part of: ${state.parentInfo}`);
  for (const s of state.subtasks) lines.push(`${s.done ? SUB_DONE : SUB_OPEN} ${s.title}`);
  if (state.priority !== "none") lines.push(`Priority: ${state.priority}`);
  if (state.deadline) lines.push(`Deadline: ${state.deadline}`);
  lines.push(BLOCK_CLOSE);
  const block = lines.join("\n");
  return head ? `${head}\n\n${block}` : block;
}

/** Bracket dialect: optional `-`/`*` bullet + leading spaces, then `[ ]`/`[x]`/`[]`,
 *  then a non-empty title. Whitespace-tolerant; depth is NEVER interpreted. */
const BRACKET_RE = /^\s*(?:[-*]\s*)?\[([ xX]?)\]\s*(\S.*?)\s*$/;

/**
 * Decode the human projection. Returns the user's free notes (with consumed
 * bracket lines and the machine block removed) plus the BRACKET EDITS the user
 * made. Emoji/display lines inside the block are dropped (machine echo). A bracket
 * line is recognised ANYWHERE (so a user can type `[ ] foo` into an event with no
 * prior block) — safe because brackets are explicit syntax and depth is ignored.
 *
 * Call this only for events that ARE our tasks; plain events keep verbatim notes.
 */
export function parseManagedBlock(description: string): { userNotes: string; bracketEdits: BracketEdit[] } {
  const rawLines = (description ?? "").split("\n");
  let openIdx = -1;
  let closeIdx = -1;
  for (let i = 0; i < rawLines.length; i++) {
    const t = rawLines[i]!.trim();
    if (openIdx < 0 && (t === BLOCK_OPEN || t === LEGACY_OPEN || t === LEGACY_HEADER)) {
      openIdx = i;
      continue;
    }
    if (openIdx >= 0 && (t === BLOCK_CLOSE || t === LEGACY_CLOSE)) {
      closeIdx = i;
      break;
    }
  }
  // Legacy header has no close sentinel → the block runs to the end.
  const inBlock = (i: number) => openIdx >= 0 && i > openIdx && (closeIdx < 0 || i < closeIdx);
  const isSentinel = (i: number) => i === openIdx || i === closeIdx;

  const bracketEdits: BracketEdit[] = [];
  const noteLines: string[] = [];
  for (let i = 0; i < rawLines.length; i++) {
    if (isSentinel(i)) continue;
    const raw = rawLines[i]!;
    const m = BRACKET_RE.exec(raw);
    if (m && m[2]!.trim()) {
      bracketEdits.push({ title: m[2]!.trim(), done: m[1]!.toLowerCase() === "x" });
      continue; // a user signal — consumed, never a note
    }
    if (inBlock(i)) continue; // emoji / display lines = machine echo
    noteLines.push(raw);
  }
  return { userNotes: noteLines.join("\n").trim(), bracketEdits };
}

/** True when the description still carries un-canonicalised bracket lines (used by
 *  the write-back to converge them to emoji). */
export function blockHasBracketEdits(description: string): boolean {
  return parseManagedBlock(description).bracketEdits.length > 0;
}

/** Legacy v2 fallback: scan the description for `Priority:`/`Deadline:` display
 *  lines. Used ONLY when an event has no `tcState` yet (old format). */
export function parseLegacyExtras(description: string): { priority: Priority; deadline: string | null } {
  let priority: Priority = "none";
  let deadline: string | null = null;
  for (const raw of (description ?? "").split("\n")) {
    const line = raw.trim();
    const pr = /^priority:\s*(none|low|medium|high)\s*$/i.exec(line);
    if (pr) priority = pr[1]!.toLowerCase() as Priority;
    const dl = /^deadline:\s*(\d{4}-\d{2}-\d{2})\s*$/i.exec(line);
    if (dl) deadline = dl[1]!;
  }
  return { priority, deadline };
}

// ── extendedProperties (machine state of truth) ──────────────────────────────

export function buildTaskExtendedProps(state: TaskState): Record<string, string> {
  return {
    [TC_EXT_TASK]: "1",
    [TC_EXT_VER]: CURRENT_VER,
    [TC_EXT_STATE]: JSON.stringify(serializeState(state)),
  };
}

/**
 * Deterministic content hash of an event body, used as the {@link TC_EXT_REV}
 * write nonce. The nonce itself is excluded (it is stamped INTO the body it
 * describes), as is `tcVer` (a version bump must not read as a content change —
 * the version check is a separate, explicit staleness signal).
 *
 * Key order is normalised so two devices, or the same device across sessions,
 * hash identical bodies identically. FNV-1a: not cryptographic — this only has
 * to distinguish "my own last write" from "someone edited this", and a hash
 * collision degrades to today's behaviour (an inbound apply), never to data loss.
 */
export function eventBodyRev(body: unknown): string {
  let hash = 0x811c9dc5;
  const s = canonicalJson(body);
  for (let i = 0; i < s.length; i++) {
    hash ^= s.charCodeAt(i);
    hash = Math.imul(hash, 0x01000193) >>> 0;
  }
  return hash.toString(36);
}

function canonicalJson(value: unknown): string {
  if (value === null || value === undefined) return "null";
  if (typeof value !== "object") return JSON.stringify(value);
  if (Array.isArray(value)) return `[${value.map(canonicalJson).join(",")}]`;
  const obj = value as Record<string, unknown>;
  const keys = Object.keys(obj)
    // `id` is excluded too: an insert body carries the client-assigned id while
    // every later PATCH body does not, and a nonce that differed between them
    // would make the write-back fire on every single pull.
    .filter((k) => k !== TC_EXT_REV && k !== TC_EXT_VER && k !== "id" && obj[k] !== undefined)
    .sort();
  return `{${keys.map((k) => `${JSON.stringify(k)}:${canonicalJson(obj[k])}`).join(",")}}`;
}

/** The write nonce stamped on a Google event, or null for events we never wrote
 *  (or wrote before v4). */
export function parseEventRev(priv?: Record<string, string>): string | null {
  return priv?.[TC_EXT_REV] ?? null;
}

/**
 * A Google event id derived deterministically from the TASK id and its link
 * GENERATION, so creating the event is IDEMPOTENT across devices.
 *
 * Two devices can hold the same unlinked task and run a Google pass before
 * either has synced — both then insert, and the calendar ends up with two events
 * for one task. Nothing downstream can fully repair that: picking a winner
 * leaves the loser orphaned in Google, and the next pull imports it as another
 * task. With a derived id the second insert collides (Google answers 409) and
 * that device adopts the first device's event instead of minting its own.
 *
 * The GENERATION (`link_rev`) is part of the id because an event id cannot be
 * reused once its event is cancelled. Delete→restore, un-date→re-date and
 * calendar moves all unlink first, which bumps the generation — so the re-created
 * event gets a FRESH deterministic id instead of colliding with its own
 * tombstone. Without it, both devices would hit the tombstone, both fall back to
 * a random id, and the two-device race would return for every re-creation.
 * `link_rev` is synced, so both devices derive the same id.
 *
 * Generation 0 is encoded as the bare id, which keeps every event created before
 * this scheme addressable.
 *
 * Google's event-id charset is base32hex — lowercase `a`–`v` and `0`–`9`, length
 * 5–1024. A dash-stripped UUID is pure hex, so it already qualifies, and
 * `Number.toString(32)` lands in the same alphabet. Returns null for any task id
 * that does not (an imported or hand-made id); the caller then falls back to
 * letting Google assign one.
 */
export function eventIdForTask(taskId: string, generation = 0): string | null {
  const base = taskId.replace(/-/g, "").toLowerCase();
  if (!/^[a-v0-9]+$/.test(base)) return null;
  // The base is fixed-length per task, so appending the generation stays
  // unambiguous without a separator.
  const candidate = generation > 0 ? `${base}${Math.floor(generation).toString(32)}` : base;
  return candidate.length >= 5 && candidate.length <= 1024 ? candidate : null;
}

function serializeState(s: TaskState): Record<string, unknown> {
  return {
    p: s.priority,
    dl: s.deadline,
    ...(s.parentInfo ? { pi: s.parentInfo } : {}),
    s: s.subtasks.map((t) => ({ i: t.id, t: t.title, d: t.done })),
  };
}

export interface ParsedTaskState {
  /** This event is one of our tasks (tcTask set, or any decodable machine state). */
  isTask: boolean;
  /** A current-shape `tcState` was present (vs legacy/absent → caller may fall back). */
  hasState: boolean;
  ver: string | null;
  state: TaskState;
}

export function parseTaskState(priv?: Record<string, string>): ParsedTaskState {
  if (!priv) return { isTask: false, hasState: false, ver: null, state: emptyState() };
  const isTask = priv[TC_EXT_TASK] === "1";
  const ver = priv[TC_EXT_VER] ?? null;

  const rawState = priv[TC_EXT_STATE];
  if (rawState) {
    try {
      const o = JSON.parse(rawState) as {
        p?: unknown;
        dl?: unknown;
        pi?: unknown;
        s?: { i?: unknown; t?: unknown; d?: unknown }[];
      };
      const state: TaskState = {
        priority: normPriority(o.p),
        deadline: typeof o.dl === "string" ? o.dl : null,
        subtasks: Array.isArray(o.s)
          ? o.s.map((x) => ({ id: String(x.i ?? ""), title: String(x.t ?? ""), done: !!x.d }))
          : [],
        ...(typeof o.pi === "string" && o.pi ? { parentInfo: o.pi } : {}),
      };
      return { isTask: true, hasState: true, ver, state }; // tcState present ⇒ ours
    } catch {
      /* malformed — fall through to legacy / empty */
    }
  }

  const legacy = priv[TC_EXT_SUBTASKS];
  if (legacy) {
    try {
      const arr = JSON.parse(legacy);
      if (Array.isArray(arr)) {
        return {
          isTask: true,
          hasState: false,
          ver,
          state: {
            priority: "none",
            deadline: null,
            subtasks: arr.map((x: { i?: unknown; t?: unknown; d?: unknown }) => ({
              id: String(x.i ?? ""),
              title: String(x.t ?? ""),
              done: !!x.d,
            })),
          },
        };
      }
    } catch {
      /* ignore */
    }
  }

  return { isTask, hasState: false, ver, state: emptyState() };
}

function normPriority(p: unknown): Priority {
  return p === "low" || p === "medium" || p === "high" ? p : "none";
}

/** Structured equality — compares machine state, NEVER description text. Immune to
 *  Google whitespace normalisation, which is what makes write-back convergent. */
export function stateEquals(a: TaskState, b: TaskState): boolean {
  if (a.priority !== b.priority) return false;
  if ((a.deadline ?? null) !== (b.deadline ?? null)) return false;
  if ((a.parentInfo ?? "") !== (b.parentInfo ?? "")) return false;
  if (a.subtasks.length !== b.subtasks.length) return false;
  for (let i = 0; i < a.subtasks.length; i++) {
    const x = a.subtasks[i]!;
    const y = b.subtasks[i]!;
    if (x.id !== y.id || x.title !== y.title || x.done !== y.done) return false;
  }
  return true;
}
